import { Year } from "@/features/pe_aldd";
import { PeaSummaryResponse, YearSeries } from "@/store/services/facades/types/pea";
import { Rcp } from "@/types/rcp";
import { clamp } from "lodash";
import { memo, useMemo } from "react";
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { MetricKey } from "./common/utils";
export type MiniHazardSparklineProps = {
  data: PeaSummaryResponse;
  hazardId: string;
  rcp: Rcp;
  selectedYear: Year;
  field: MetricKey;
  color?: string;
  height?: number;
  domainMax?: number;
};
type Point = {
  year: number;
  value: number;
};

// Show every 5 years from 1990 to 2100
const YEARS: number[] = Array.from({
  length: (2100 - 1990) / 5 + 1
}, (_, i) => 1990 + i * 5);
const toPercent = (v: number | null | undefined): number => v === null || v === undefined ? 0 : v * 100;
const MiniHazardSparklineBase = ({
  data,
  hazardId,
  rcp,
  selectedYear,
  field,
  color = "#6B7280",
  height = 24,
  domainMax
}: MiniHazardSparklineProps) => {
  const row = useMemo(() => data.find(r => r.climate_scenario === rcp && String(r.hazard) === String(hazardId)), [data, rcp, hazardId]);
  const points: Point[] = useMemo(() => {
    const series = row?.[field] as YearSeries ?? {};
    return YEARS.map(y => ({
      year: y,
      value: toPercent(series[y as Year])
    }));
  }, [row, field]);

  // Highlight the bar closest to the selectedYear (round to nearest 5-year tick)
  const nearestTickYear = useMemo(() => {
    const rounded = Math.round(Number(selectedYear) / 5) * 5;
    return clamp(rounded, 1990, 2100);
  }, [selectedYear]);
  return <div style={{
    width: 120,
    height
  }} data-sentry-component="MiniHazardSparklineBase" data-sentry-source-file="MiniHazardSparkline.tsx">
      <ResponsiveContainer data-sentry-element="ResponsiveContainer" data-sentry-source-file="MiniHazardSparkline.tsx">
        <BarChart data={points} margin={{
        top: 2,
        right: 2,
        bottom: 2,
        left: 2
      }} barCategoryGap="5%" data-sentry-element="BarChart" data-sentry-source-file="MiniHazardSparkline.tsx">
          <XAxis dataKey="year" tick={false} tickLine={false} axisLine={{
          stroke: color
        }} height={1} data-sentry-element="XAxis" data-sentry-source-file="MiniHazardSparkline.tsx" />
          <YAxis hide domain={[0, domainMax ?? 1]} data-sentry-element="YAxis" data-sentry-source-file="MiniHazardSparkline.tsx" />
          <Bar dataKey="value" isAnimationActive={false} data-sentry-element="Bar" data-sentry-source-file="MiniHazardSparkline.tsx">
            {points.map(p => <Cell key={p.year} fill={color} opacity={p.year === nearestTickYear ? 1 : 0.25} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>;
};
export const MiniHazardSparkline = memo(MiniHazardSparklineBase);